import React, { useContext } from 'react';
import { makeStyles } from 'tss-react/mui';
import { BusinessCollectionMenu } from '../BusinessCollectionMenu/BusinessCollectionMenu';
import EDView from '../../../../../../BaseComponents/View/EDView';
import BusinessDataSet from '../BusinessDataSet/BusinessDataSet';
import {
  IClientAssociationModel,
  IClientBusinessModel,
} from '../../../../../../Types/ClientModelTypes';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import { IAssociationListConfig } from './AssociationList';
import { ISortInfo } from '../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import EDMiniDrawer from '../../../../../../BaseComponents/Drawers/MiniDrawer/EDMiniDrawer';
import { IUIUrval } from '../../../../../../Types/UrvalTypes';
import { IDetaljurvalInfo } from '../../../../../../Components/Urval/Components/HitIndicator/UrvalHitIndicator';
import { ObjektVyGruppContext } from '../ObjektVyKort/ObjektVyKortContext';
import BusinessCollectionMenuButton from '../BusinessCollectionMenu/BusinessCollectionMenuButton';
import { createPortal } from 'react-dom';
import { VisningsTypContext } from '../VisningsTypContext/VisningsTypContext';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import ListFilteringComponent from '../../ListFilteringComponent/ListFilteringComponent';
import { IFilterAttribute } from '../../../Const/Delsystem';
import { GridEventListener } from '@mui/x-data-grid-pro';

interface IProps {
  collection: IClientBusinessModel;
  association: IClientAssociationModel | undefined;
  associationPath?: string;
  columns: readonly IColumnDef[];
  setColumns: (newColumns: readonly IColumnDef[]) => void;
  isSmall: boolean;
  config: IAssociationListConfig;
  refetch: () => void;
  loading: boolean;
  editable?: boolean;
  selectDetail: (id: TSelectedDetail) => void;
  selectNewDetail: (customDetailData?: unknown) => void;
  selectedId: TSelectedDetail;
  sortInfo: ISortInfo<ICollectionDataType>;
  filteredSortInfo?: ISortInfo<ICollectionDataType>;
  updateSortInfo: (info: ISortInfo<ICollectionDataType>) => void;
  inlineDetail?: React.ReactNode;
  baseCollection: IClientBusinessModel;
  baseId: string | null;
  urval: IUIUrval | null;
  setUrval: (newUrval: IUIUrval | null) => void;
  urvalInfo?: IDetaljurvalInfo;
  numberOfRows?: number;
  /** Sätt höjden för att alla rader ska få plats */
  autoHeight?: boolean;
  onAddNewItem?: (newItem: ICollectionDataType) => void;
  onDeleteItem?: (item: ICollectionDataType) => void;
  onRowClick?: GridEventListener<'rowClick'>;
  selectionGrid?: boolean;
  newItems: ICollectionDataType[] | undefined;
  rutinId: number;
  searchText?: string;
  setSearchText?: (newValue: string) => void;
  filterAttributes?: IFilterAttribute[];
}

const useStyles = makeStyles()(
  theme =>
    ({
      paper: {
        top: 136,
        height: 'calc(100% - 136px)',
      },
      contentSubContainer: {
        width: '100%',
        flex: 1,
        paddingLeft: 4,
      },
      contentSubContainerCard: {
        flex: 1,
        paddingLeft: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
          paddingRight: theme.spacing(1),
        },
      },
      contentContainerCard: {
        paddingLeft: '8px',
      },
      contentSubContainerEmpty: {
        minHeight: 120,
      },
      contentSubContainer1Row: {
        minHeight: 120,
      },
      contentSubContainer2Rows: {
        minHeight: 160,
      },
      contentSubContainer3Rows: {
        minHeight: 220,
      },
      contentSubContainer4OrMoreRows: {
        minHeight: 270,
      },
      contentSubContainerCapped: {},
      contentSubContainerSmall: {},
      contentSubContainerMaxHeight: {},
      flexContainer: {
        display: 'flex',
        flex: 1,
        width: '100%',
        height: '100%',
      },
    } as const)
);

const AssociationListMasterView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles(undefined, {
    props: props as any,
  });
  // return props.config.inCard ? (
  return (
    <div className={classes.flexContainer}>
      <MasterView {...props} />
    </div>
  );
  // ) : (
  //   <MasterView {...props} />
  // );
};

const MasterView: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes, cx } = useStyles(undefined, {
    props: props as any,
  });
  const menuContext = useContext(ObjektVyGruppContext);
  const { visningsTyp } = useContext(VisningsTypContext);
  const listNotVirtual = visningsTyp === 'lista' && props.numberOfRows;
  const numberRowsClassName =
    visningsTyp === 'lista'
      ? ''
      : props.numberOfRows === 0
      ? classes.contentSubContainerEmpty
      : props.numberOfRows === 1
      ? classes.contentSubContainer1Row
      : props.numberOfRows === 2
      ? classes.contentSubContainer2Rows
      : props.numberOfRows === 3
      ? classes.contentSubContainer3Rows
      : classes.contentSubContainer4OrMoreRows;
  return (
    <>
      {!props.config.inCard ? (
        !props.isSmall ? (
          <EDMiniDrawer
            open={false}
            close={() => {}}
            toolbar={null}
            hideToolbarDivider
          >
            <Menu {...props} />
          </EDMiniDrawer>
        ) : null
      ) : (
        <>
          {menuContext.menuRef.current !== null &&
            createPortal(
              <Menu {...props} button />,
              menuContext.menuRef.current
            )}
        </>
      )}
      <EDView
        className={cx(
          visningsTyp === 'kort'
            ? classes.contentSubContainerCard
            : classes.contentSubContainer,
          numberRowsClassName,
          {
            [classes.contentSubContainerCapped]:
              !listNotVirtual && props.config.inCard,
            [classes.contentSubContainerSmall]: props.isSmall,
          }
        )}
      >
        {props.filterAttributes && (
          <ListFilteringComponent
            onSearchTextChanged={(text: string): void => {
              props.setSearchText?.(text);
            }}
            searchText={props.searchText ?? ''}
            sortedData={props.sortInfo}
            filterAttributes={props.filterAttributes}
            filterAttributesPlacholder={`Filtrera på ${props.filterAttributes
              .map(attr => attr.label)
              .join(', ')
              .replace(/, (?!.*, )/, ' eller ')}`}
            visningsTyp={visningsTyp}
            listlength={props.sortInfo.rows.length}
            sortedLength={props.filteredSortInfo?.rows.length ?? 0}
          />
        )}
        <BusinessDataSet
          collection={props.collection}
          association={props.association}
          associationPath={props.associationPath}
          columns={props.columns}
          urval={props.urval}
          refetch={props.refetch}
          loading={props.loading}
          editable={props.config.integrated}
          selectDetail={props.selectDetail}
          selectedId={props.selectedId}
          selectionGrid={props.selectionGrid}
          inlineDetail={props.inlineDetail}
          selected={[]}
          toggleSelected={() => {}}
          inCard={props.config.inCard}
          autoHeight={props.autoHeight}
          useFunktionsMenyItems={props.config.useFunctionMenuItems}
          hideDefaultFunktionsMenyItems={
            props.config.hideDefaultFunktionsMenyItems
          }
          selectNewDetail={props.selectNewDetail}
          sortedData={props.filteredSortInfo ?? props.sortInfo}
          updateSortOrder={props.updateSortInfo}
          disabled={props.config.disabled}
          newItems={props.newItems}
          onDeleteItem={props.onDeleteItem}
          onRowClick={props.onRowClick}
          sumColumns={props.config.sumColumns}
          isCellDisabled={props.config.isCellDisabled}
          settingsId={`AssociationList-${props.rutinId}-${props.collection.CollectionName}-${props.association?.AssociationName}`}
        />
      </EDView>
    </>
  );
};

const Menu: React.FC<
  React.PropsWithChildren<IProps & { button?: boolean }>
> = props =>
  props.button ? (
    <BusinessCollectionMenuButton
      collection={props.collection}
      columns={props.columns}
      setColumns={props.setColumns}
      urval={props.urval}
      setUrval={props.setUrval}
      refetch={props.refetch}
      sortedData={props.sortInfo}
      updateSortOrder={props.updateSortInfo}
      baseCollection={props.baseCollection}
      baseId={props.baseId}
      detaljurvalInfo={props.urvalInfo}
      inCard={Boolean(props.config.inCard)}
      onAddNewItem={props.onAddNewItem}
      menuItems={props.config.businessCollectionMenuItems}
      highlightedBusinessCollectionMenuItem={
        props.config.highlightedBusinessCollectionMenuItem
      }
      rutinId={props.rutinId}
      datasetLength={props.sortInfo.rows.length}
      rows={props.sortInfo.rows}
    />
  ) : (
    <BusinessCollectionMenu
      collection={props.collection}
      columns={props.columns}
      setColumns={props.setColumns}
      urval={props.urval}
      setUrval={props.setUrval}
      refetch={props.refetch}
      sortedData={props.sortInfo}
      updateSortOrder={props.updateSortInfo}
      baseCollection={props.baseCollection}
      baseId={props.baseId}
      detaljurvalInfo={props.urvalInfo}
      rutinId={props.rutinId}
      datasetLength={props.sortInfo.rows.length}
      rows={props.sortInfo.rows}
    />
  );

export default AssociationListMasterView;
